import { lazy, Suspense, useEffect, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Banner, Button } from "bizpandit-ui";
import { apiErrorMessage } from "@/api/apiError";
import { attendanceService, browserPosition, clockIst, locationPermission } from "../services/attendance.service";

/** How to switch location on — shown whenever the browser gave none. */
function LocationHelp() {
  return (
    <div className="mt-2 text-[12.5px] leading-relaxed">
      <div className="font-semibold">To check in from the office, let this site see your location:</div>
      <ul className="list-disc pl-5 mt-1 space-y-0.5">
        <li><b>Browser:</b> click the icon at the left of the address bar (the slider or padlock) → <b>Site settings</b> → <b>Location: Allow</b>, then reload the page.</li>
        <li><b>Windows:</b> Settings → Privacy &amp; security → <b>Location</b> → turn on <b>Location services</b> and <b>Let desktop apps access your location</b>.</li>
        <li><b>Mac:</b> System Settings → Privacy &amp; Security → <b>Location Services</b> → turn on for your browser.</li>
        <li><b>Phone:</b> allow location for your browser in the phone's settings.</li>
      </ul>
      <div className="mt-1">Working away from the office? Your manager can approve the request below, or approve work from home, and the next attempt goes through.</div>
    </div>
  );
}

// Lazy: the gate dialog is one request per log-out, not per page.
const ReconcileShiftDialog = lazy(() => import("./ReconcileShiftDialog"));

/**
 * BA §3.1(a): "logging into the dashboard from within the geofenced area" is
 * the attendance capture. The app asks the browser for a position once per
 * session after login and posts it; the SERVER judges the fence. A refused
 * position is sent as the reason it was refused — never treated as "inside".
 *
 * Renders nothing for a user with no employee record (40 of 42 users are
 * clients; the query is one request per session for them). Shown above the
 * page, like the job-health banner: attendance is a condition of the day,
 * not of whichever screen is open.
 */
export function AttendanceCheckInBanner() {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["hr", "attendance", "me"], queryFn: () => attendanceService.me(), staleTime: 60_000, retry: false });
  const [result, setResult] = useState<{ tone: "success" | "warning" | "danger"; text: string; noLocation?: boolean } | null>(null);
  const [permission, setPermission] = useState<"granted" | "prompt" | "denied" | "unknown" | null>(null);
  useEffect(() => { void locationPermission().then(setPermission); }, []);
  const [reconciling, setReconciling] = useState(false);
  const attempted = useRef(false);

  const checkIn = useMutation({
    mutationFn: async () => attendanceService.checkIn(await browserPosition()),
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: ["hr", "attendance"] });
      if (r.blocked) {
        setPermission(null); void locationPermission().then(setPermission);
        setResult({ tone: "danger", text: `Check-in refused: ${r.reason}. A location-exception request has been raised for your manager; approval lets the next attempt through.`, noLocation: r.distanceM == null });
      }
      else if (r.alreadyCheckedIn) setResult(null);
      else setResult({ tone: r.record?.status === "LATE" ? "warning" : "success", text: `Checked in at ${clockIst(r.record?.check_in_at)} — ${r.record?.status === "LATE" ? `late by ${r.record.late_minutes} min` : r.record?.status === "WFH" ? "work from home" : "on time"}.` });
    },
    onError: (e) => setResult({ tone: "danger", text: apiErrorMessage(e, "Check-in failed.") }),
  });
  // BA §6: the log-out is gated. A 409 means the server found open items —
  // open the gate dialog rather than reporting an error; the server stays
  // the only judge of whether the gate is open.
  const checkOut = useMutation({
    mutationFn: async () => attendanceService.checkOut(await browserPosition()),
    onSuccess: (r) => { qc.invalidateQueries({ queryKey: ["hr"] }); setReconciling(false); setResult({ tone: "success", text: `Checked out at ${clockIst(r.record.check_out_at)}${r.reconciled ? " — shift reconciled." : "."}` }); },
    onError: (e) => {
      const status = (e as { status?: number })?.status;
      if (status === 409) { setReconciling(true); setResult(null); return; }
      setResult({ tone: "danger", text: apiErrorMessage(e, "Check-out failed.") });
    },
  });

  // Once per browser session, automatically — the login IS the capture.
  // Only when the browser will answer without asking: a permission question
  // raised on page load (no click) is the one Chrome hides and then blocks.
  // Otherwise the Check in button asks, from a click, where the browser shows
  // a proper prompt.
  useEffect(() => {
    if (!q.data?.needsCheckIn || attempted.current || permission === null) return;
    if (q.data.geofenceConfigured && !q.data.exempt && (permission === "prompt" || permission === "denied")) return;
    let already = false;
    try { already = sessionStorage.getItem("hr.checkin.attempted") === "1"; } catch { /* storage unavailable */ }
    if (already) return;
    attempted.current = true;
    try { sessionStorage.setItem("hr.checkin.attempted", "1"); } catch { /* ignore */ }
    checkIn.mutate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q.data?.needsCheckIn, permission]);

  const me = q.data;
  if (!me?.employee) return null;
  const rec = me.record;
  const canOut = !!rec?.check_in_at && !rec.check_out_at;

  const gate = reconciling ? (
    <Suspense fallback={null}>
      <ReconcileShiftDialog onClose={() => setReconciling(false)} onReady={() => checkOut.mutate()} />
    </Suspense>
  ) : null;

  if (result) {
    return (
      <div className="px-5 pt-4">
        <Banner tone={result.tone}>
          <span className="flex items-center gap-3 flex-wrap">{result.text}
            {result.tone === "danger" && me.needsCheckIn && <Button type="button" size="sm" variant="outline" onClick={() => checkIn.mutate()} disabled={checkIn.isPending}>Try again</Button>}
            <button type="button" className="ml-auto text-[12px] underline opacity-70" onClick={() => setResult(null)}>dismiss</button>
          </span>
          {result.noLocation && <LocationHelp />}
        </Banner>
        {gate}
      </div>
    );
  }
  if (me.needsCheckIn) {
    return (
      <div className="px-5 pt-4">
        <Banner tone="info">
          <span className="flex items-center gap-3 flex-wrap">
            Not checked in today{me.shift ? ` · shift ${me.shift.name}` : ""}.{me.geofenceConfigured && !me.exempt ? " Your position is checked against the office geofence." : ""}
            {me.geofenceConfigured && !me.exempt && permission === "prompt" ? " When your browser asks, choose Allow." : ""}
            <Button type="button" size="sm" onClick={() => checkIn.mutate()} disabled={checkIn.isPending}>{checkIn.isPending ? "Checking in…" : "Check in"}</Button>
          </span>
          {me.geofenceConfigured && !me.exempt && permission === "denied" && <LocationHelp />}
        </Banner>
      </div>
    );
  }
  if (canOut) {
    return (
      <div className="px-5 pt-4 flex justify-end gap-3">
        <button type="button" onClick={() => setReconciling(true)} className="text-[12px] text-navy2/70 hover:text-navy2 underline">reconcile shift</button>
        <button type="button" onClick={() => checkOut.mutate()} disabled={checkOut.isPending} className="text-[12px] text-navy2/70 hover:text-navy2 underline">
          Checked in {clockIst(rec!.check_in_at)}{rec!.status === "LATE" ? " (late)" : ""} · {checkOut.isPending ? "checking out…" : "check out"}
        </button>
        {gate}
      </div>
    );
  }
  return gate;
}
